import { loading, scrollLock } from 'helpers';
import {
  getCartSectionNames,
  renderCartSections,
  updateCartCount,
} from 'cart-refresh';

const ACTIVE_CLASS = 'ab-added-cart-upsell-b';
const DEFAULT_VARIANT_TITLE = 'Default Title';
const RECOMMENDATIONS_LIMIT = 10;

function isActive() {
  return document.documentElement.classList.contains(ACTIVE_CLASS) || Shopify.designMode;
}

function normalizeImageUrl(value) {
  const url = typeof value === 'string'
    ? value
    : value?.url || value?.src || value?.preview_image?.src || '';

  if (url.startsWith('//')) return `https:${url}`;
  return url;
}

function resizeImage(url, width) {
  if (!url) return '';
  const resizedUrl = new URL(url, window.location.origin);
  resizedUrl.searchParams.set('width', width);
  return resizedUrl.toString();
}

function money(cents) {
  const amount = Number(cents || 0) / 100;
  const hasFraction = Math.round(amount * 100) % 100 !== 0;
  return new Intl.NumberFormat(Shopify.locale || 'en-US', {
    style: 'currency',
    currency: Shopify.currency?.active || 'USD',
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(amount);
}

class AddedCartUpsell {
  constructor(dialog) {
    this.dialog = dialog;
    this.recommendations = dialog.querySelector('[data-upsell-recommendations]');
    this.track = dialog.querySelector('[data-upsell-track]');
    this.liveRegion = dialog.querySelector('[data-upsell-live-region]');
    this.previousButton = dialog.querySelector('[data-upsell-previous]');
    this.nextButton = dialog.querySelector('[data-upsell-next]');
    this.closeButton = dialog.querySelector('.added-cart-upsell__close');
    this.cartButton = dialog.querySelector('.added-cart-upsell__button--primary');
    this.continueButton = dialog.querySelector('[data-upsell-continue]');
    this.mobileHeading = dialog.querySelector('.added-cart-upsell__heading-mobile');
    this.returnFocus = null;
    this.openCartOnClose = false;
    this.recommendationRequest = 0;

    document.addEventListener('bambi:product-added', this.handleProductAdded.bind(this));
    this.dialog.addEventListener('close', this.handleClose.bind(this));
    this.dialog.addEventListener('click', this.handleDialogClick.bind(this));
    this.track.addEventListener('scroll', this.updateArrowState.bind(this), { passive: true });
    this.track.addEventListener('click', this.handleTrackClick.bind(this));
    this.previousButton.addEventListener('click', () => this.scrollRecommendations(-1));
    this.nextButton.addEventListener('click', () => this.scrollRecommendations(1));
    this.closeButton.addEventListener('click', () => {
      if (isActive()) this.openCartOnClose = true;
    });
    this.cartButton.addEventListener('click', (event) => {
      if (!isActive()) return;
      event.preventDefault();
      this.openCartOnClose = true;
      this.dialog.close();
    });
    this.continueButton.addEventListener('click', () => this.dialog.close());

    if (Shopify.designMode && this.dialog.open) {
      this.loadBundleProducts();
    }
  }

  getAddedItem(state) {
    return state?.items?.[0] || state;
  }

  handleProductAdded(event) {
    const item = this.getAddedItem(event.detail?.state);
    if (!isActive() || !item?.product_id) return;

    const hasBundleProducts = this.loadBundleProducts();
    event.preventDefault();
    this.returnFocus = event.detail?.trigger || document.activeElement;
    this.populateAddedItem(item);
    if (!hasBundleProducts) this.loadShopifyRecommendations(item.product_id);

    const sourceDialog = event.detail?.trigger?.closest('dialog[open]');
    if (sourceDialog && sourceDialog !== this.dialog) {
      sourceDialog.addEventListener('close', () => this.open(), { once: true });
      sourceDialog.close();
    } else {
      requestAnimationFrame(() => this.open());
    }
  }

  open() {
    if (!this.dialog.isConnected) document.body.append(this.dialog);
    if (!this.dialog.open) this.dialog.showModal();
    this.updateArrowState();
    scrollLock.enable();
    this.dialog.focus();
  }

  handleClose() {
    scrollLock.disable();
    if (!Shopify.designMode) this.dialog.remove();

    if (this.openCartOnClose) {
      this.openCartOnClose = false;
      requestAnimationFrame(() => {
        const cartDrawerTrigger = document.querySelector('[data-drawer-open="[data-mini-cart]"]');
        if (cartDrawerTrigger) {
          cartDrawerTrigger.click();
        } else if (this.returnFocus?.isConnected) {
          this.returnFocus.focus();
        }
      });
      return;
    }

    if (this.returnFocus?.isConnected) this.returnFocus.focus();
  }

  handleDialogClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  populateAddedItem(item) {
    const image = this.dialog.querySelector('[data-added-cart-item-image]');
    const placeholder = this.dialog.querySelector('[data-added-cart-item-image-placeholder]');
    const title = item.product_title || item.title || '';
    const imageUrl = normalizeImageUrl(item.featured_image || item.image);
    const itemUrl = item.url || '#';

    this.dialog.querySelectorAll('[data-added-cart-item-link]').forEach((link) => {
      link.href = itemUrl;
    });
    this.dialog.querySelector('[data-added-cart-item-title]').textContent = title;
    this.dialog.querySelector('[data-added-cart-item-price]').textContent = money(item.final_price ?? item.price);

    const variant = this.dialog.querySelector('[data-added-cart-item-variant]');
    const variantTitle = item.variant_title || '';
    variant.textContent = variantTitle;
    variant.hidden = !variantTitle || variantTitle === DEFAULT_VARIANT_TITLE;

    if (imageUrl) {
      image.src = resizeImage(imageUrl, 140);
      image.alt = title;
      image.hidden = false;
      if (placeholder) placeholder.hidden = true;
    } else {
      image.hidden = true;
      if (placeholder) placeholder.hidden = false;
    }
  }

  loadBundleProducts() {
    this.recommendationRequest += 1;
    const bundle = document.querySelector('.shopify-section--bundle-maker-v2 [data-bmv2]');
    const products = bundle
      ? [...bundle.querySelectorAll('[data-bmv2-product]')].filter(
        (item) => item.dataset.bmv2Available === 'true'
      )
      : [];

    this.track.replaceChildren();
    this.previousButton.hidden = true;
    this.nextButton.hidden = true;
    const fragment = document.createDocumentFragment();

    products.forEach((product) => fragment.append(this.buildProductCard(product)));

    if (!fragment.childNodes.length) {
      this.recommendations.hidden = true;
    } else {
      this.mobileHeading.textContent = 'Complete the bundle';
      this.track.setAttribute('aria-label', 'Bundle products');
      this.recommendations.hidden = false;
      this.track.append(fragment);
    }

    this.track.scrollLeft = 0;
    requestAnimationFrame(() => this.updateArrowState());
    return products.length > 0;
  }

  async loadShopifyRecommendations(productId) {
    const requestId = ++this.recommendationRequest;
    const endpoint = new URL(`${WYATT.routes.root}recommendations/products.json`, window.location.origin);
    endpoint.searchParams.set('product_id', productId);
    endpoint.searchParams.set('limit', RECOMMENDATIONS_LIMIT);
    endpoint.searchParams.set('intent', 'related');

    const status = document.createElement('p');
    status.className = 'added-cart-upsell__status';
    status.textContent = 'Loading recommendations…';
    this.mobileHeading.textContent = 'You May Also Like';
    this.track.setAttribute('aria-label', 'Recommended products');
    this.track.replaceChildren(status);
    this.recommendations.hidden = false;

    try {
      const response = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to load recommendations');

      const data = await response.json();
      if (requestId !== this.recommendationRequest) return;

      const cards = (data.products || [])
        .filter((product) => product.available && !product.vendor?.toLowerCase().includes('re:do'))
        .map((product) => this.buildRecommendationCard(product))
        .filter(Boolean);

      this.track.replaceChildren(...cards);
      this.recommendations.hidden = cards.length === 0;
      this.track.scrollLeft = 0;
      requestAnimationFrame(() => this.updateArrowState());
    } catch (error) {
      if (requestId !== this.recommendationRequest) return;
      this.track.replaceChildren();
      this.recommendations.hidden = true;
      this.liveRegion.textContent = error.message;
    }
  }

  buildRecommendationCard(product) {
    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const selectedVariant = availableVariants[0];
    if (!selectedVariant) return null;

    const sourceItem = document.createElement('div');
    sourceItem.dataset.bmv2Product = product.id;
    sourceItem.dataset.bmv2Title = product.title;
    sourceItem.dataset.bmv2VariantId = selectedVariant.id;
    sourceItem.dataset.bmv2VariantPrice = selectedVariant.price;

    const selectedImage = selectedVariant.featured_image || product.featured_image;
    const image = document.createElement('img');
    image.dataset.bmv2Image = '';
    image.src = normalizeImageUrl(selectedImage);
    image.alt = selectedImage?.alt || product.title;
    sourceItem.append(image);

    if (availableVariants.length > 1) {
      availableVariants.forEach((variant) => {
        const variantImage = variant.featured_image || product.featured_image;
        const swatch = document.createElement('button');
        swatch.dataset.bmv2Swatch = variant.id;
        swatch.dataset.bmv2SwatchPrice = variant.price;
        swatch.dataset.bmv2SwatchName = variant.option1 || variant.title;
        swatch.dataset.bmv2SwatchAvailable = 'true';
        swatch.dataset.bmv2SwatchImage = normalizeImageUrl(variantImage);
        swatch.dataset.bmv2SwatchImageAlt = variantImage?.alt || variant.title;
        sourceItem.append(swatch);
      });
    }

    return this.buildProductCard(sourceItem);
  }

  buildProductCard(sourceItem) {
    const selectedVariantId = sourceItem.dataset.bmv2VariantId || '';
    const sourceImage = sourceItem.querySelector('[data-bmv2-image]');
    const imageUrl = normalizeImageUrl(sourceImage?.currentSrc || sourceImage?.src);
    const card = document.createElement('article');
    card.className = 'added-cart-upsell__card';
    card.dataset.productId = sourceItem.dataset.bmv2Product || '';

    const imageLink = document.createElement('div');
    imageLink.className = 'added-cart-upsell__card-image-link';

    const image = document.createElement('img');
    image.className = 'added-cart-upsell__card-image';
    image.width = 121;
    image.height = 121;
    image.loading = 'lazy';
    image.alt = sourceImage?.alt || sourceItem.dataset.bmv2Title || '';
    image.src = resizeImage(imageUrl, 242);
    imageLink.append(image);

    const title = document.createElement('p');
    title.className = 'added-cart-upsell__card-title';
    title.textContent = sourceItem.dataset.bmv2Title || '';

    const swatches = this.buildSwatches(sourceItem, card, selectedVariantId);

    const price = document.createElement('p');
    price.className = 'added-cart-upsell__card-price';
    price.dataset.upsellPrice = '';
    price.textContent = money(sourceItem.dataset.bmv2VariantPrice);

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'added-cart-upsell__card-add';
    addButton.dataset.upsellAdd = '';
    addButton.dataset.variantId = selectedVariantId;
    addButton.textContent = '+ Add';
    addButton.disabled = !selectedVariantId;

    card.append(imageLink, title, swatches, price, addButton);
    return card;
  }

  buildSwatches(sourceItem, card, selectedVariantId) {
    const wrapper = document.createElement('div');
    wrapper.className = 'added-cart-upsell__swatches';

    const seen = new Set();
    const sourceSwatches = [...sourceItem.querySelectorAll('[data-bmv2-swatch]')].filter((sourceSwatch) => {
      const value = sourceSwatch.dataset.bmv2SwatchName;
      const variantId = sourceSwatch.dataset.bmv2Swatch;
      if (
        sourceSwatch.dataset.bmv2SwatchAvailable !== 'true'
        || !value
        || !variantId
        || seen.has(value)
      ) return false;
      seen.add(value);
      return true;
    });

    const visibleSwatches = sourceSwatches.slice(0, 5);
    const selectedSwatch = sourceSwatches.find(
      (sourceSwatch) => sourceSwatch.dataset.bmv2Swatch === selectedVariantId
    );
    if (selectedSwatch && !visibleSwatches.includes(selectedSwatch)) {
      visibleSwatches[visibleSwatches.length - 1] = selectedSwatch;
    }

    visibleSwatches.forEach((sourceSwatch) => {
      const value = sourceSwatch.dataset.bmv2SwatchName;
      const variantId = sourceSwatch.dataset.bmv2Swatch;

      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'added-cart-upsell__swatch button-reset';
      swatch.dataset.variantId = variantId;
      swatch.dataset.variantPrice = sourceSwatch.dataset.bmv2SwatchPrice || '';
      swatch.dataset.variantImage = normalizeImageUrl(sourceSwatch.dataset.bmv2SwatchImage);
      swatch.dataset.variantImageAlt = sourceSwatch.dataset.bmv2SwatchImageAlt || value;
      swatch.setAttribute('aria-label', value);
      swatch.setAttribute('aria-pressed', String(variantId === selectedVariantId));

      if (swatch.dataset.variantImage) {
        swatch.style.backgroundImage = `url("${resizeImage(swatch.dataset.variantImage, 48)}")`;
      } else {
        const sourceDot = sourceSwatch.querySelector('.bmv2__swatch-dot > *');
        if (sourceDot?.tagName === 'IMG') {
          swatch.style.backgroundImage = `url("${sourceDot.currentSrc || sourceDot.src}")`;
        } else if (sourceDot) {
          swatch.style.background = sourceDot.style.background || sourceDot.style.backgroundColor;
        }
      }
      wrapper.append(swatch);
    });

    wrapper.addEventListener('click', (event) => {
      const swatch = event.target.closest('.added-cart-upsell__swatch');
      if (!swatch) return;
      wrapper.querySelectorAll('.added-cart-upsell__swatch').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === swatch));
      });
      card.querySelector('[data-upsell-add]').dataset.variantId = swatch.dataset.variantId;
      card.querySelector('[data-upsell-price]').textContent = money(swatch.dataset.variantPrice);
      if (swatch.dataset.variantImage) {
        const cardImage = card.querySelector('.added-cart-upsell__card-image');
        cardImage.src = resizeImage(swatch.dataset.variantImage, 242);
        cardImage.alt = swatch.dataset.variantImageAlt;
      }
    });

    return wrapper;
  }

  async handleTrackClick(event) {
    const button = event.target.closest('[data-upsell-add]');
    if (!button || button.disabled || !button.dataset.variantId) return;

    let added = false;
    loading.add(button);
    button.disabled = true;
    try {
      const response = await fetch(`${WYATT.routes.cart_add_url}.js`, {
        ...WYATT.config.fetch.post,
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: getCartSectionNames(),
        }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || 'Unable to add item');

      renderCartSections(state.sections);
      const cartResponse = await fetch(`${WYATT.routes.cart_url}.js`, { cache: 'no-store' });
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        window.WYATT.shopify.cart = cart;
        updateCartCount(cart);
      }
      added = true;
      this.liveRegion.textContent = 'Item added to cart.';
      document.dispatchEvent(new Event('cart:updated'));
    } catch (error) {
      button.disabled = false;
      this.liveRegion.textContent = error.message;
    } finally {
      loading.remove(button);
      if (added) button.textContent = 'Added';
    }
  }

  scrollRecommendations(direction) {
    const card = this.track.querySelector('.added-cart-upsell__card');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({
      left: direction * ((card?.offsetWidth || this.track.clientWidth) + gap),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  updateArrowState() {
    const canScroll = this.track.scrollWidth > this.track.clientWidth + 1;
    this.previousButton.hidden = !canScroll || this.track.scrollLeft <= 1;
    this.nextButton.hidden = !canScroll || this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
  }
}

const template = document.querySelector('[data-added-cart-upsell-template]');
const dialog = template
  ? template.content.querySelector('[data-added-cart-upsell]')
  : document.querySelector('[data-added-cart-upsell]');
if (dialog) new AddedCartUpsell(dialog);
